import React, { useContext } from "react";
import { ThemeContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";
import { Bar } from "react-chartjs-2";
import theme from "../../index.scss";
import TriggerChartOptions from "./TriggerChartOptions";
import TriggerNoStackedOptions from "./TriggerNoStackedOptions";
import { parse, format } from "date-fns";
import getDaysAgo from "../../lib/getDaysAgo";
import fillInDates from "../../lib/fillInDates";

const TriggerChart = ({ data, height, stacked }) => {
  const { dark } = useContext(ThemeContext);
  const { t } = useTranslation();

  const tCategory = (subcategory) => {
    const key = `socialdeterminants.tabs.${subcategory.toLowerCase().replace(/ /g, "_")}`;
    const translated = t(key);
    return translated !== key ? translated : subcategory;
  };

  const tDay = (dayName) => {
    const key = `days.${dayName.toLowerCase()}`;
    const translated = t(key);
    return translated !== key ? translated : dayName;
  };

  const tTrigger = (factor) => {
    const categories = ["wellness","travel","screen_time","environmental","dietary","situational","other"];
    const safe = (factor || "").trim().toLowerCase()
      .replace(/ /g,"_").replace(/\//g,"_").replace(/,/g,"")
      .replace(/:/g,"").replace(/'/g,"").replace(/[()]/g,"").replace(/-/g,"_")
      .substring(0, 60);
    for (const cat of categories) {
      const key = `trigger.${cat}.${safe}`;
      const translated = t(key);
      if (translated !== key) return translated;
    }
    return factor;
  };

  const colors = {
    wellness: theme.chart1,
    environmental: theme.chart2,
    "screen time": theme.chart3,
    travel: theme.chart4,
    other: theme.chart5,
    situational: theme.chart6,
    dietary: theme.chart7,
  };

  let dataset = {
    wellness: [],
    environmental: [],
    "screen time": [],
    travel: [],
    situational: [],
    dietary: [],
    other: [],
  };

  let triggers = data?.filter(
    ({ category, symptom_date }) =>
      category === "SDOH" && new Date(symptom_date) > getDaysAgo(7)
  );

  triggers &&
    triggers.forEach((trigger) => {
      dataset[trigger.subcategory.toLowerCase()].push(trigger);
    });

  const dataForChart = { datasets: [] };

  const setDataForSubcategory = (subcategoryData) => {
    let DataSortByDate = {};
    subcategoryData.forEach((item) => {
      if (DataSortByDate[item.symptom_date]) {
        DataSortByDate[item.symptom_date].push(item.factor);
      } else {
        DataSortByDate[item.symptom_date] = [item.factor];
      }
    });

    let structuredData = [];
    for (let date in DataSortByDate) {
      const formattedDate = date.slice(0, 10);
      structuredData.push({
        x: formattedDate,
        y: DataSortByDate[date].length,
        detail: DataSortByDate[date],
      });
    }
    return structuredData;
  };

  for (let subcategory in dataset) {
    dataForChart.datasets.push({
      label: tCategory(subcategory),
      data: setDataForSubcategory(dataset[subcategory]),
      backgroundColor: colors[subcategory],
    });
  }

  fillInDates(dataForChart.datasets, 7);

  TriggerChartOptions.title.fontColor = dark ? theme.darkModeText : theme.black;
  TriggerChartOptions.title.text = t("triggerchart.title");
  TriggerChartOptions.legend = {
    labels: { fontColor: dark ? theme.darkModeText : theme.black },
  };
  TriggerChartOptions.scales.xAxes[0].ticks = {
    fontColor: dark ? theme.darkModeText : theme.black,
  };
  TriggerChartOptions.scales.yAxes[0].ticks = {
    beginAtZero: true,
    stepSize: 1,
    fontColor: dark ? theme.darkModeText : theme.black,
  };

  return stacked == false ? (
    <Bar data={dataForChart} height={height} options={{
      ...TriggerNoStackedOptions,
      tooltips: {
        ...TriggerNoStackedOptions.tooltips,
        callbacks: {
          title: (items) => tDay(items[0]?.xLabel || ""),
          label: (item, data) => {
            const pointData = data.datasets[item.datasetIndex].data[item.index].y;
            return pointData?.detail?.map((d) => tTrigger(d)).join(", ") ?? "";
          },
        },
      },
    }} />
  ) : (
    <Bar data={dataForChart} height={height} options={{
      ...TriggerChartOptions,
      tooltips: {
        ...TriggerChartOptions.tooltips,
        callbacks: {
          title: (items) => tDay(items[0]?.xLabel || ""),
          label: (item, data) => {
            const pointData = data.datasets[item.datasetIndex].data[item.index].y;
            return pointData?.detail?.map((d) => tTrigger(d)).join(", ") ?? "";
          },
        },
      },
    }} />
  );
};

export default TriggerChart;